import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * A hook for managing rate limits (slow mode) with persistence across page reloads.
 * 
 * @param {string} key - A unique key for this rate limit (e.g., 'qa-slowmode')
 * @param {number} cooldownSeconds - The cooldown period in seconds
 */
export const useRateLimit = (key, cooldownSeconds = 30) => {
  const [isSlowMode, setIsSlowMode] = useState(false);
  const [timer, setTimer] = useState(0);
  const intervalRef = useRef(null);

  const getStorageKey = useCallback(() => `rate_limit_${key}`, [key]);

  const startCooldown = useCallback((customCooldown = cooldownSeconds) => {
    const now = Date.now();
    const expiry = now + (customCooldown * 1000);
    sessionStorage.setItem(getStorageKey(), expiry.toString());
    
    setIsSlowMode(true);
    setTimer(customCooldown);
    
    if (intervalRef.current) clearInterval(intervalRef.current);
    
    intervalRef.current = setInterval(() => {
      const currentTime = Date.now();
      const remaining = Math.max(0, Math.ceil((expiry - currentTime) / 1000));
      
      setTimer(remaining);
      
      if (remaining <= 0) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
        setIsSlowMode(false);
        sessionStorage.removeItem(getStorageKey());
      }
    }, 1000);
  }, [getStorageKey, cooldownSeconds]);

  useEffect(() => {
    const expiry = sessionStorage.getItem(getStorageKey());
    if (expiry) {
      const remaining = Math.max(0, Math.ceil((parseInt(expiry) - Date.now()) / 1000));
      if (remaining > 0) {
        setIsSlowMode(true);
        setTimer(remaining);
        
        intervalRef.current = setInterval(() => {
          const currentTime = Date.now();
          const rem = Math.max(0, Math.ceil((parseInt(expiry) - currentTime) / 1000));
          
          setTimer(rem);
          
          if (rem <= 0) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = null;
            setIsSlowMode(false);
            sessionStorage.removeItem(getStorageKey());
          }
        }, 1000);
      } else {
        sessionStorage.removeItem(getStorageKey());
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [getStorageKey]);

  return {
    isSlowMode,
    timer,
    startCooldown,
    remainingSeconds: timer
  };
};

export default useRateLimit;
